import React from 'react';
import { cn } from '@/lib/utils';

/**
 * Facts as label-cell / value-cell rows.
 *
 * Generalised from OrderDetail/OrderFactsBand, which already proved the point:
 * twelve values that used to occupy two stacked cards now read across four
 * columns in about the height of one. A fact row is 26px. The rounded chip it
 * replaces — caption above value, `bg-slate-50 rounded-lg px-2.5 py-1.5` — is 44px.
 *
 * `auto-fit` rather than a fixed column count, so the same band works in a
 * full-width header and in a 20rem rail without the caller choosing a breakpoint.
 */
export function FactBand({ children, min = '15rem', className, ...props }) {
  return (
    <div
      className={cn(
        'grid gap-x-3 gap-y-px overflow-hidden rounded-lg border border-slate-200 bg-white p-1 shadow-card',
        className
      )}
      style={{ gridTemplateColumns: `repeat(auto-fit, minmax(${min}, 1fr))` }}
      {...props}
    >
      {children}
    </div>
  );
}

/** One label cell butted against one value cell. */
export function Fact({ label, children, title, className, valueClassName }) {
  return (
    <div className={cn('flex min-w-0 items-stretch text-xs', className)} title={title}>
      <span className="flex min-w-[5.5rem] shrink-0 items-center whitespace-nowrap border-r border-slate-200 bg-slate-100 px-2 py-1 font-medium text-slate-600">
        {label}
      </span>
      <span className={cn('flex min-w-0 flex-1 items-center gap-1.5 truncate px-2 py-1 text-slate-800 tabular-nums', valueClassName)}>
        {children === null || children === undefined || children === '' ? '—' : children}
      </span>
    </div>
  );
}

/**
 * A fact you can type into. The label cell stays; the value cell becomes the
 * input, with no border of its own so the row keeps its height.
 *
 * Lifted from JobCard/JobHeaderBand, where the pattern already lets a supervisor
 * fill in twelve job facts without any of them looking like a form field.
 */
export function EditableFact({
  label, value, onChange, type = 'text', placeholder, disabled, options, className,
}) {
  if (disabled) return <Fact label={label} className={className}>{value}</Fact>;

  return (
    <div className={cn('flex min-w-0 items-stretch text-xs', className)}>
      <span className="flex min-w-[5.5rem] shrink-0 items-center whitespace-nowrap border-r border-slate-200 bg-slate-100 px-2 py-1 font-medium text-slate-600">
        {label}
      </span>
      {options ? (
        <select
          value={value ?? ''}
          onChange={e => onChange(e.target.value)}
          aria-label={label}
          className="min-w-0 flex-1 bg-transparent px-2 py-1 text-slate-800 focus:bg-blue-50 focus:outline-none focus:ring-1 focus:ring-inset focus:ring-brand-blue"
        >
          <option value="">—</option>
          {options.map(o => (
            <option key={o.value ?? o} value={o.value ?? o}>{o.label ?? o}</option>
          ))}
        </select>
      ) : (
        <input
          type={type}
          value={value ?? ''}
          onChange={e => onChange(e.target.value)}
          placeholder={placeholder}
          aria-label={label}
          className="min-w-0 flex-1 bg-transparent px-2 py-1 text-slate-800 tabular-nums placeholder:text-slate-300 focus:bg-blue-50 focus:outline-none focus:ring-1 focus:ring-inset focus:ring-brand-blue"
        />
      )}
    </div>
  );
}

export default FactBand;
